import { MonitorSmartphone, TrendingUp, Gem, Check, ArrowRight, ShieldCheck, Rocket, Users } from "lucide-react";
import { Reveal, SectionLabel } from "@/components/Reveal";

const OFFERS = [
  {
    icon: MonitorSmartphone,
    title: "High-Ticket Websites",
    tagline: "Sites that sell before you speak",
    points: ["Cinematic, conversion-first design", "Blazing-fast, SEO-ready builds", "Booking, CRM & payment integrations", "Built to justify premium pricing"],
    testid: "hto-card-web",
  },
  {
    icon: TrendingUp,
    title: "Performance Marketing",
    tagline: "Every rupee tracked to revenue",
    points: ["Meta & Google ads engineered for ROAS", "High-converting funnels & landing pages", "Weekly ROI-first reporting", "Scale only what's profitable"],
    testid: "hto-card-marketing",
  },
  {
    icon: Gem,
    title: "Premium Branding",
    tagline: "Identities buyers remember",
    points: ["Strategy-led identity systems", "Luxury-grade visual language", "CGI, film & photography assets", "Brand guidelines that scale"],
    testid: "hto-card-branding",
  },
];

const PROMISES = [
  { icon: Users, title: "Senior team only", desc: "No juniors, no handoffs — specialists on every project." },
  { icon: Rocket, title: "Launch in weeks", desc: "Sprint-based delivery. Momentum from day one." },
  { icon: ShieldCheck, title: "ROI-first thinking", desc: "Design decisions tied to revenue, not vanity." },
];

export const HighTicket = () => (
  <section id="advantage" className="relative border-y border-[var(--border-subtle)] overflow-hidden">
    <div className="pointer-events-none absolute -top-40 right-0 h-[480px] w-[480px] rounded-full bg-[var(--accent-primary)] opacity-[0.05] blur-[120px]" />
    <div className="relative mx-auto max-w-[1400px] px-6 sm:px-10 py-24 lg:py-36">
      <Reveal>
        <SectionLabel>The Sanvalio Advantage</SectionLabel>
        <div className="grid gap-8 lg:grid-cols-[1.2fr_1fr] lg:items-end">
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.02]">
            Engineered to win <span className="text-gradient">high-ticket clients.</span>
          </h2>
          <p className="font-body text-[var(--text-secondary)] leading-relaxed max-w-md lg:justify-self-end">
            Premium buyers judge you in seconds. We build the website, the ads and the brand that make them say yes — before the first call.
          </p>
        </div>
      </Reveal>

      <div className="mt-14 grid gap-6 md:grid-cols-3">
        {OFFERS.map((o, i) => (
          <Reveal key={o.title} delay={i * 0.12}>
            <div data-testid={o.testid} className="group relative flex h-full flex-col rounded-3xl glass p-8 hover-card-glow overflow-hidden">
              <span className="absolute -top-14 -right-14 h-40 w-40 rounded-full bg-[var(--accent-primary)] opacity-[0.07] blur-3xl group-hover:opacity-[0.15] transition-opacity" />
              <span className="grid h-12 w-12 place-items-center rounded-2xl btn-accent"><o.icon size={22} /></span>
              <h3 className="mt-6 font-display text-2xl font-bold">{o.title}</h3>
              <p className="mt-1 font-mono text-xs uppercase tracking-[0.14em] text-[var(--accent-primary)]">{o.tagline}</p>
              <ul className="mt-6 space-y-3 flex-1">
                {o.points.map((p) => (
                  <li key={p} className="flex items-start gap-3 font-body text-sm text-[var(--text-secondary)] leading-relaxed">
                    <Check size={16} className="mt-0.5 shrink-0 text-[var(--accent-primary)]" />
                    {p}
                  </li>
                ))}
              </ul>
              <a href="#contact" className="mt-8 inline-flex items-center gap-2 font-heading font-semibold text-[var(--text-primary)] group-hover:text-[var(--accent-primary)] transition-colors">
                Get a proposal
                <ArrowRight size={17} className="group-hover:translate-x-1 transition-transform" />
              </a>
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal delay={0.15}>
        <div className="mt-12 grid gap-px overflow-hidden rounded-3xl border border-[var(--border-subtle)] sm:grid-cols-3 bg-[var(--border-subtle)]">
          {PROMISES.map((p) => (
            <div key={p.title} className="flex items-start gap-4 bg-[var(--bg-primary)] p-7">
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl border border-[var(--border-subtle)] text-[var(--accent-primary)]"><p.icon size={19} /></span>
              <span>
                <span className="block font-heading font-semibold">{p.title}</span>
                <span className="mt-1 block font-body text-sm text-[var(--text-secondary)] leading-relaxed">{p.desc}</span>
              </span>
            </div>
          ))}
        </div>
      </Reveal>
    </div>
  </section>
);
